import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  PreviewProfileTypeBlockResponse,
  PreviewTypeRemovalResponse,
  ProfileTypeStats,
  TypeRemoval,
} from '@shared/schemas';

import type { ProfileTypeBlock } from '../domain/profile-type.model';
import { closedRemoval, emptyNotice, idlePreview, initialState, type Notice } from './profile-types.state';

/**
 * Admin Profile types slice. Reducers are pure state transitions only; the load,
 * save, toggle, clear and run orchestrations live in the saga, which dispatches these
 * as it progresses. `blocksSet` is the silent refresh after an operation (no spinner);
 * `loadStarted`/`loadSucceeded`/`loadFailed` drive the first mount's.
 */
const slice = createSlice({
  name: 'profileTypes',
  initialState,
  reducers: {
    // ── Initial load lifecycle ──
    loadStarted: (state) => {
      state.loadStatus = 'loading';
    },
    loadSucceeded: (state, action: PayloadAction<ProfileTypeBlock[]>) => {
      state.blocks = action.payload;
      state.loadStatus = 'loaded';
    },
    loadFailed: (state) => {
      state.loadStatus = 'failed';
    },

    /** Silent refresh of the list (after a save, a clear or a run). */
    blocksSet: (state, action: PayloadAction<ProfileTypeBlock[]>) => {
      state.blocks = action.payload;
    },

    /**
     * Replace one type in place, keeping its position; the Automatic switch's answer,
     * so the list does not reload for a field the response already carries.
     */
    blockReplaced: (state, action: PayloadAction<ProfileTypeBlock>) => {
      const index = state.blocks.findIndex((b) => b.name === action.payload.name);
      if (index !== -1) state.blocks[index] = action.payload;
    },

    /** The overview's figures, refreshed with the list. */
    statsSet: (state, action: PayloadAction<ProfileTypeStats>) => {
      state.stats = action.payload;
    },

    // ── Busy flags ──
    saveStarted: (state) => {
      state.saving = true;
    },
    saveStopped: (state) => {
      state.saving = false;
    },
    clearStarted: (state, action: PayloadAction<string>) => {
      state.clearingName = action.payload;
    },
    clearStopped: (state) => {
      state.clearingName = null;
    },
    toggleStarted: (state, action: PayloadAction<string>) => {
      state.togglingNames.push(action.payload);
    },
    toggleStopped: (state, action: PayloadAction<string>) => {
      state.togglingNames = state.togglingNames.filter((n) => n !== action.payload);
    },

    // ── On-demand run ──
    runPreviewStarted: (state) => {
      state.runPreview = { ...idlePreview, status: 'loading' };
    },
    runPreviewSucceeded: (state, action: PayloadAction<PreviewProfileTypeBlockResponse>) => {
      state.runPreview = {
        status: 'loaded',
        count: action.payload.match_count,
        sample: action.payload.sample,
        alreadyTagged: action.payload.already_tagged_count,
        withoutCounts: action.payload.without_condition_counts,
      };
    },
    runPreviewFailed: (state) => {
      state.runPreview = { ...idlePreview, status: 'failed' };
    },
    runPreviewCleared: (state) => {
      state.runPreview = idlePreview;
    },
    // ── The editor's live count ──
    draftPreviewStarted: (state) => {
      state.draftPreview = { ...idlePreview, status: 'loading' };
    },
    draftPreviewSucceeded: (state, action: PayloadAction<PreviewProfileTypeBlockResponse>) => {
      state.draftPreview = {
        status: 'loaded',
        count: action.payload.match_count,
        sample: action.payload.sample,
        alreadyTagged: action.payload.already_tagged_count,
        withoutCounts: action.payload.without_condition_counts,
      };
    },
    draftPreviewFailed: (state) => {
      state.draftPreview = { ...idlePreview, status: 'failed' };
    },
    /** Back to untested; dispatched when the editor closes or the draft cannot be previewed. */
    draftPreviewCleared: (state) => {
      state.draftPreview = idlePreview;
    },

    runStarted: (state, action: PayloadAction<string>) => {
      state.runningName = action.payload;
    },
    runStopped: (state) => {
      state.runningName = null;
    },

    // ── Type priority (#554) ──
    typePriorityLoadStarted: (state) => {
      state.typePriority.status = 'loading';
    },
    typePriorityLoaded: (state, action: PayloadAction<string[]>) => {
      state.typePriority = {
        names: action.payload,
        saved: action.payload,
        status: 'loaded',
        saving: false,
      };
    },
    typePriorityLoadFailed: (state) => {
      state.typePriority.status = 'failed';
    },
    /**
     * Move one type one place up or down, locally.
     *
     * Local until "Save order" because the save recomputes every profile; one
     * full-table UPDATE per arrow click is not a thing to ship.
     */
    typeMoved: (state, action: PayloadAction<{ from: number; to: number }>) => {
      const { from, to } = action.payload;
      const names = state.typePriority.names;
      if (from < 0 || to < 0 || from >= names.length || to >= names.length) return;
      const [moved] = names.splice(from, 1);
      names.splice(to, 0, moved);
    },
    typePrioritySaveStarted: (state) => {
      state.typePriority.saving = true;
    },
    typePrioritySaved: (state) => {
      state.typePriority.saving = false;
      state.typePriority.saved = [...state.typePriority.names];
    },
    typePrioritySaveFailed: (state) => {
      state.typePriority.saving = false;
    },

    // ── "Review and apply" ──
    applyTargetSet: (state, action: PayloadAction<string | null>) => {
      state.applyTarget = action.payload;
    },

    // ── Removing a type from profiles (#652) ──
    removalOpened: (state, action: PayloadAction<string>) => {
      state.removal = { ...closedRemoval, target: action.payload, status: 'loading' };
    },
    removalPreviewSucceeded: (state, action: PayloadAction<PreviewTypeRemovalResponse>) => {
      // A preview landing after the dialog closed, or on another type, is dropped.
      if (state.removal.target !== action.payload.tag) return;
      state.removal.status = 'loaded';
      state.removal.preview = action.payload;
    },
    removalPreviewFailed: (state) => {
      if (state.removal.target !== null) state.removal.status = 'failed';
    },
    removalStarted: (state) => {
      state.removal.removing = true;
    },
    removalStopped: (state) => {
      state.removal.removing = false;
    },
    removalClosed: (state) => {
      state.removal = closedRemoval;
    },
    removalsSet: (state, action: PayloadAction<TypeRemoval[]>) => {
      state.removals = action.payload;
    },
    undoStarted: (state, action: PayloadAction<string>) => {
      state.undoingId = action.payload;
    },
    undoStopped: (state) => {
      state.undoingId = null;
    },

    // ── Operation notice (snackbars) ──
    noticeSet: (state, action: PayloadAction<Notice>) => {
      state.notice = action.payload;
    },
    noticeCleared: (state) => {
      state.notice = emptyNotice;
    },
  },
});

export const profileTypesActions = slice.actions;
export const profileTypesReducer = slice.reducer;
